import React, { useState, useContext } from 'react';
import { Col, Row, Container } from 'reactstrap';
import styles from './PlanBenefits.module.css';
import { ReactComponent as ArrowIcon } from '../../../../assets/images/arrow-cheveron-down.svg';
import { ReactComponent as WandIcon } from '../../../../assets/images/wand.svg';
import { ReactComponent as TrophyIcon } from '../../../../assets/images/trophy.svg';
import { ReactComponent as DollarIcon } from '../../../../assets/images/dollar-sign.svg';
import { ReactComponent as ShieldIcon } from '../../../../assets/images/shield-check.svg';
import { ReactComponent as BillsIcon } from '../../../../assets/images/bills.svg';
import { ReactComponent as CheckmarkIcon } from '../../../../assets/images/checkmark-badge.svg';
import { ReactComponent as HourGlassIcon } from '../../../../assets/images/hourglass.svg';
import { ReactComponent as PiggyBankIcon } from '../../../../assets/images/piggy-bank.svg';
import { ReactComponent as RefrigeratorDollarIcon } from '../../../../assets/images/refrigerator-dollarsign.svg';
import { ReactComponent as ServicerIcon } from '../../../../assets/images/servicer.svg';
import { ReactComponent as SupportIcon } from '../../../../assets/images/support.svg';
import { useMediaQuery } from 'react-responsive';
import getClientContent from '../../../_BrandingProvider/brandContent';
import { BrandingContext } from '../../../../components/contexts/ContractContext';
import { stripPhoneNumber } from '../../../shared/util';
import ClientPhone from '../../../shared/ClientPhone';
import useLoadAtTop from '../../../_ReactHooks/useLoadAtTop';
import CookiesIcon from '../../../../assets/images/Vendor/NJR/cookies_new.jpg';

const items = [
    {
        image: <CheckmarkIcon />,
        text: (
            <p className='mb-0'>
                Mechanical and <br />
                Electrical Failures
            </p>
        )
    },
    {
        image: <HourGlassIcon />,
        text: (
            <p>
                Covers Older Products <br />
                (No Age Limit)
            </p>
        )
    },
    {
        image: <PiggyBankIcon />,
        text: (
            <p>
                Save on <br />
                Repairs
            </p>
        )
    },
    {
        image: <SupportIcon />,
        text: (
            <p>
                Easy Claim <br />
                Setup
            </p>
        )
    },
    {
        image: <RefrigeratorDollarIcon />,
        text: (
            <p>
                Low Trade <br />
                Service Fee
            </p>
        )
    },
    {
        image: <ServicerIcon />,
        text: (
            <p>
                Knowledgeable, <br />
                Courteous Technicians
            </p>
        )
    },
    {
        image: <WandIcon />,
        text: (
            <p>
                Quick Diagnosis and <br />
                Explanation of Repair
            </p>
        )
    },
    {
        image: <BillsIcon />,
        text: <p>Convenient Payment Options Available</p>
    }
];

const CarouselItem = ({ data: { image, text }, itemNo }) => {
    const isMobile = useMediaQuery({ maxWidth: '1024px' });

    return (
        <Col
            md='3'
            xs='6'
            className={`${styles.benefit} ${isMobile ? 'p-0 ' : ''}`}
            onClick={() => console.log(items[itemNo])}
        >
            {image}
            {text}
        </Col>
    );
};

const IconBulletPoint = ({ icon, title, text, lastOfList = false }) => {
    return (
        <div className={`d-flex ${!lastOfList && 'mb-5'}`}>
            <div className={styles.bulletIcons}>{icon}</div>
            <div className={styles.bulletDesc}>
                <h2 className='h4'>{title}</h2>
                <p>{text}</p>
            </div>
        </div>
    );
};

const PlanBenefits = () => {
    useLoadAtTop();
    const context = useContext(BrandingContext);
    const { clientId } = context;
    const { variableText } = getClientContent(clientId);
    const customerService = variableText.customerService.phone;
    const [indexPos, setIndexPos] = useState(0);
    const isMobile = useMediaQuery({ maxWidth: '1024px' });

    const min = indexPos * 4,
        max = 4 + indexPos * 4;
    return (
        <Container className='p-0'>
            <div className={styles.planBenefits}>
                <h1 className={styles.cardHeading}>Service Plan Benefits.</h1>
                <p className={styles.tagLine}>
                    You rely on your appliances every day; our plans provide a reliable service solution for covered
                    repairs.
                </p>
                {isMobile ? (
                    <Row>
                        {items.map((data, itemNo) => (
                            <CarouselItem
                                data={data}
                                itemNo={itemNo}
                                key={itemNo}
                            />
                        ))}
                    </Row>
                ) : (
                    <div className='position-relative'>
                        <button
                            className={`${styles.btnLeft} ${max === 4 ? styles.disbleBtnLeft : styles.enableBtn}`}
                            onClick={() => setIndexPos((prev) => prev - 1)}
                            disabled={max === 4}
                            aria-label='button left corousel'
                        >
                            <ArrowIcon />
                        </button>
                        <Row className={styles.benfitCorousel}>
                            {items.slice(min, max).map((data, itemNo) => (
                                <CarouselItem
                                    data={data}
                                    itemNo={itemNo}
                                    key={itemNo}
                                />
                            ))}
                        </Row>
                        <button
                            className={`${styles.btnRight} ${max === items.length ? styles.disbleBtnRight : styles.enableBtn}`}
                            onClick={() => setIndexPos((prev) => prev + 1)}
                            disabled={max === items.length}
                            aria-label='button right corousel'
                        >
                            <ArrowIcon />
                        </button>
                    </div>
                )}
            </div>

            <Row className={'m-0'}>
                <Col className={styles.bulletPoints}>
                    <h1>A Simple Service Solution.</h1>
                    <IconBulletPoint
                        icon={<DollarIcon />}
                        title='Bundle and Save Up to 18%'
                        text='Cover all the appliances you rely on under one plan.'
                    />
                    <IconBulletPoint
                        icon={<ShieldIcon />}
                        title='All Brands, No Age Limits'
                        text='Reliable protection available on covered products, regardless of age.'
                    />
                    <IconBulletPoint
                        icon={<TrophyIcon />}
                        title='Local Service Professionals'
                        text='Service scheduled at your convenience with authorized technicians you can trust.'
                        lastOfList
                    />
                </Col>

                <Col
                    className={styles.sideImage}
                    lg='5'
                >
                    <img
                        src={CookiesIcon}
                        role='presentation'
                    />
                </Col>
            </Row>
            <div className={styles.helpCard}>
                <h1>Still Deciding?</h1>
                <p>
                    We're here to help! Check out our <a href='/faq'>FAQs</a> or call us at&nbsp;&nbsp;
                    <ClientPhone department='customerService' />.
                </p>
            </div>
        </Container>
    );
};

export default PlanBenefits;
